import React, { useState, useEffect, useId, useRef, useCallback } from 'react';
import { User, Affiliate, LoginResponse } from '../types';
import { authService } from '../services/authService';
import { Lock, User as UserIcon, Loader2, Building, BookOpen } from 'lucide-react';
import { getDefaultAffiliateByDomain } from '../utils/domainAffiliate';
import { isTauri } from '../utils/tauri';
import { loadSavedLogin, saveLogin, clearSavedLogin, clearSavedPassword } from '../services/credentialService';
import { DeviceGateScreen, DeviceGateKind, deviceGateKindOf } from './DeviceGateScreen';

interface LoginProps {
  onLoginSuccess: (user: User, token: string) => void;
  /** 사용 설명서 열기 (로그인 전에도 열람 가능) */
  onHelpClick?: () => void;
}

export const Login: React.FC<LoginProps> = ({ onLoginSuccess, onHelpClick }) => {
  const fid = useId();
  const [userid, setUserid] = useState('');
  const [pwd, setPwd] = useState('');
  const [affiliateId, setAffiliateId] = useState('');
  const [affiliates, setAffiliates] = useState<Affiliate[]>([]);
  const [isLoadingAffiliates, setIsLoadingAffiliates] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [lockedAffiliate, setLockedAffiliate] = useState<string | null>(null);
  // Tauri: '자동 로그인'(아이디+비밀번호 DPAPI 저장) / 웹: '아이디 저장'(아이디만 localStorage)
  const [rememberMe, setRememberMe] = useState(false);
  const [isAutoLogin, setIsAutoLogin] = useState(false);
  // 마운트당 1회만 자동 로그인 시도 (실패 시 사용자가 버튼을 눌러야 재시도)
  const autoLoginAttemptedRef = useRef(false);
  // 기기 신뢰: 비밀번호는 맞았는데 기기가 막힌 경우의 안내 화면. 자격증명은 메모리에만 들고
  // '승인 확인'·'승인 요청'이 같은 자격증명으로 다시 로그인한다(저장하지 않는다).
  const [gate, setGate] = useState<{ kind: DeviceGateKind; response: LoginResponse } | null>(null);
  const gateCredsRef = useRef<{ uid: string; pw: string; aff: string; remember: boolean } | null>(null);

  /**
   * 실제 로그인. 성공 시 remember에 따라 자격증명 저장/삭제.
   * auto=true(자동 로그인)가 자격증명 오류(INVALID_CREDENTIALS)로 거부될 때만 저장된 비밀번호를 지워
   * 다음 실행 때 다시 시도하지 않는다. 그 밖의 실패(시도 제한·서버 오류·네트워크)는 자격증명을 유지한다 —
   * 서버가 회복되면 채워진 필드로 로그인 버튼만 누르면 되고, 다음 실행 때는 다시 자동 로그인된다.
   */
  const performLogin = async (
    uid: string,
    pw: string,
    aff: string,
    opts: { auto: boolean; remember: boolean; requestApproval?: boolean; label?: string }
  ) => {
    setIsLoading(true);
    setIsAutoLogin(opts.auto);
    setError('');
    try {
      const response = await authService.login(uid, pw, aff, { requestApproval: opts.requestApproval, label: opts.label });

      // 기기 신뢰 — 비밀번호는 맞았으므로 저장된 자동 로그인 비밀번호는 지우지 않는다(아래 화이트리스트와 같은 원칙)
      const gateKind = response.success ? null : deviceGateKindOf(response.code);
      if (gateKind) {
        gateCredsRef.current = { uid, pw, aff, remember: opts.remember };
        setGate({ kind: gateKind, response });
        return;
      }

      if (response.success && response.user && response.token) {
        gateCredsRef.current = null;
        setGate(null);
        if (opts.remember) {
          await saveLogin({ affiliateId: aff, userid: uid, pwd: isTauri() ? pw : null, autoLogin: true });
        } else {
          await clearSavedLogin();
        }
        onLoginSuccess(response.user, response.token);
      } else {
        if (opts.auto) {
          // 비밀번호가 틀렸다는 서버 판정일 때만 지운다(화이트리스트). 예전엔 NOT_APPROVED만 빼는
          // 블랙리스트라 429(시도 제한)·5xx(DB 장애)·네트워크 오류에도 지워져, 서버 재시작이나 PC 여러 대가
          // 한꺼번에 접속하는 순간 걸린 사람은 비밀번호를 다시 입력해야 했다. 승인 대기(NOT_APPROVED)도
          // 유지한다 — 승인 즉시 자동 로그인이 되어야 한다.
          if (response.code === 'INVALID_CREDENTIALS') {
            await clearSavedPassword();
          }
          setError(response.message
            ? `자동 로그인하지 못했습니다. ${response.message}`
            : '자동 로그인하지 못했습니다. 아이디와 비밀번호를 확인해 주세요.');
        } else {
          setError(response.message || '로그인하지 못했습니다. 아이디와 비밀번호를 확인해 주세요.');
        }
      }
    } catch (err) {
      setError(opts.auto
        ? '자동 로그인하지 못했습니다. 잠시 후 로그인 버튼을 눌러 주세요.'
        : '로그인하지 못했습니다. 잠시 후 다시 시도해 주세요.');
    } finally {
      setIsLoading(false);
      setIsAutoLogin(false);
    }
  };

  // 계열사 목록 로딩 → 저장된 로그인 정보 프리필 → (Tauri) 자동 로그인
  useEffect(() => {
    const init = async () => {
      setIsLoadingAffiliates(true);
      const list = await authService.getAffiliates();
      setAffiliates(list);

      // 계열사 자동 결정 (도메인 또는 빌드 환경변수)
      const domainDefault = getDefaultAffiliateByDomain();
      const buildDefault = process.env.REACT_APP_DEFAULT_AFFILIATE || null;
      const locked = domainDefault || buildDefault;
      const lockedMatch = locked ? list.find((a) => a.id === locked) : undefined;

      let effectiveAffiliate = '';
      if (lockedMatch) {
        effectiveAffiliate = lockedMatch.id;
        setLockedAffiliate(lockedMatch.id);
      } else if (!locked && list.length > 0) {
        effectiveAffiliate = list[0].id;
      }
      setAffiliateId(effectiveAffiliate);
      setIsLoadingAffiliates(false);

      const saved = await loadSavedLogin();
      if (!saved) return;
      setUserid(saved.userid);
      setRememberMe(saved.autoLogin);
      // 저장된 계열사는 도메인/빌드 고정이 없을 때만 적용 (목록에 있는 것만)
      if (!lockedMatch && saved.affiliateId && list.some((a) => a.id === saved.affiliateId)) {
        effectiveAffiliate = saved.affiliateId;
        setAffiliateId(effectiveAffiliate);
      }
      if (isTauri() && saved.pwd && saved.autoLogin && !autoLoginAttemptedRef.current) {
        autoLoginAttemptedRef.current = true;
        setPwd(saved.pwd);
        await performLogin(saved.userid, saved.pwd, effectiveAffiliate, { auto: true, remember: true });
      }
    };
    init();
  }, []);

  // 대기 화면의 '승인 확인'(주기 확인 포함)과 미등록 화면의 '승인 요청'
  const recheckGate = useCallback(() => {
    const c = gateCredsRef.current;
    if (!c) return;
    performLogin(c.uid, c.pw, c.aff, { auto: false, remember: c.remember });
  }, []); // eslint-disable-line
  const requestDeviceApproval = (label: string) => {
    const c = gateCredsRef.current;
    if (!c) return;
    performLogin(c.uid, c.pw, c.aff, { auto: false, remember: c.remember, requestApproval: true, label });
  };
  const leaveGate = () => {
    gateCredsRef.current = null;
    setGate(null);
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userid || !pwd || isLoading) return;
    await performLogin(userid, pwd, affiliateId, { auto: false, remember: rememberMe });
  };

  const showAffiliateDropdown = !lockedAffiliate && affiliates.length > 1;
  const rememberLabel = isTauri() ? '자동 로그인' : '아이디 저장';

  return (
    <div className="flex-1 bg-slate-900 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
        <div className="p-8 bg-slate-50 border-b border-slate-100 text-center">
          <img src="/icon.png" alt="INA-TALK" className="h-16 w-16 rounded-2xl mx-auto mb-4 shadow-lg" />
          <h1 className="text-2xl font-bold text-slate-800">INA-TALK</h1>

        </div>

        {gate ? (
          <DeviceGateScreen
            kind={gate.kind}
            response={gate.response}
            busy={isLoading}
            onRequest={requestDeviceApproval}
            onCheckAgain={recheckGate}
            onBack={leaveGate}
          />
        ) : (
        <form onSubmit={handleSubmit} className="p-8 space-y-6">
          {error && (
            <div className="p-3 text-sm text-brand-600 bg-brand-50 border border-brand-100 rounded-lg text-center">
              {error}
            </div>
          )}

          <div className="space-y-4">
            {showAffiliateDropdown && (
              <div>
                <label htmlFor={`${fid}-aff`} className="block text-sm font-medium text-slate-700 mb-1">계열사</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Building className="h-5 w-5 text-slate-400" />
                  </div>
                  <select id={`${fid}-aff`}
                    value={affiliateId}
                    onChange={(e) => setAffiliateId(e.target.value)}
                    className="block w-full pl-10 pr-3 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-brand-400 focus:border-brand-500 transition-colors bg-white"
                    disabled={isLoadingAffiliates}
                  >
                    {affiliates.map((aff) => (
                      <option key={aff.id} value={aff.id}>
                        {aff.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            <div>
              <label htmlFor={`${fid}-uid`} className="block text-sm font-medium text-slate-700 mb-1">인트라넷 아이디</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <UserIcon className="h-5 w-5 text-slate-400" />
                </div>
                <input id={`${fid}-uid`}
                  type="text"
                  name="username"
                  autoComplete="username"
                  value={userid}
                  onChange={(e) => setUserid(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-brand-400 focus:border-brand-500 transition-colors"
                  placeholder="아이디 입력"
                  required
                />
              </div>
            </div>

            <div>
              <label htmlFor={`${fid}-pwd`} className="block text-sm font-medium text-slate-700 mb-1">비밀번호</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-slate-400" />
                </div>
                <input id={`${fid}-pwd`}
                  type="password"
                  name="password"
                  autoComplete="current-password"
                  value={pwd}
                  onChange={(e) => setPwd(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-brand-400 focus:border-brand-500 transition-colors"
                  placeholder="비밀번호 입력"
                  required
                />
              </div>
            </div>
          </div>

          <label htmlFor={`${fid}-remember`} className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer select-none w-fit">
            <input id={`${fid}-remember`}
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              className="h-4 w-4 accent-brand-600"
            />
            {rememberLabel}
          </label>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full flex items-center justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-400 disabled:opacity-70 disabled:cursor-not-allowed transition-all"
          >
            {isLoading ? (
              <>
                <Loader2 className="animate-spin -ml-1 mr-2 h-4 w-4" />
                {isAutoLogin ? '자동 로그인 중...' : '인증 중...'}
              </>
            ) : (
              '로그인'
            )}
          </button>
        </form>
        )}

        <div className="px-8 py-4 bg-slate-50 border-t border-slate-100 text-center space-y-2">
          <p className="text-xs text-slate-500">
            사내 인트라넷 계정으로 로그인하세요.
          </p>
          {/* PC 버전 설치파일 링크는 두지 않는다 — 로그인 후 설정/프로필 메뉴에서만 받는다 (사외 노출 차단) */}
          <div className="flex items-center justify-center">
            <button
              type="button"
              onClick={onHelpClick}
              className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-brand-700 transition-colors"
            >
              <BookOpen className="h-3.5 w-3.5" />
              사용 설명서
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
